const WISHLIST_KEY = 'shopify-wishlist';
const WISHLIST_EVENT = 'wishlist-update';

function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setWishlist(handles) {
  const list = Array.isArray(handles) ? handles : [];
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { wishlist: list } }));
  return list;
}

function setWishlistSilently(handles) {
  const list = Array.isArray(handles) ? handles : [];
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  return list;
}

function addToWishlist(handle) {
  const list = getWishlist();
  const h = (handle || '').toString().trim();
  if (!h || list.includes(h)) return list;
  list.push(h);
  return setWishlist(list);
}

function removeFromWishlist(handle) {
  const list = getWishlist().filter((id) => id !== (handle || '').toString().trim());
  return setWishlist(list);
}

function toggleWishlist(handle) {
  const h = (handle || '').toString().trim();
  if (!h) return getWishlist();
  const list = getWishlist();
  if (list.includes(h)) return setWishlist(list.filter((id) => id !== h));
  list.push(h);
  return setWishlist(list);
}

function isInWishlist(handle) {
  return getWishlist().includes((handle || '').toString().trim());
}

window.Wishlist = {
  get: getWishlist,
  add: addToWishlist,
  remove: removeFromWishlist,
  toggle: toggleWishlist,
  has: isInWishlist,
  key: WISHLIST_KEY,
  event: WISHLIST_EVENT
};

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = null;
    this.inner = null;
    this.itemsContainer = null;
    this.emptyState = null;
    this.closeBtn = null;
    this.strings = {};
    this._addedVariantIds = new Set();
  }

  connectedCallback() {
    this.strings = window.wishlistStrings || {};
    this.overlay = this.querySelector('.wishlist-drawer__overlay');
    this.inner = this.querySelector('.wishlist-drawer__inner');
    this.itemsContainer = this.querySelector('.wishlist-drawer__items');
    this.emptyState = this.querySelector('.wishlist-drawer__empty');
    this.closeBtn = this.querySelector('.wishlist-drawer__close');

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (e) => e.code === 'Escape' && this.close());

    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-wishlist-open]');
      if (openBtn) {
        e.preventDefault();
        this.open();
      }
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = toggleBtn.getAttribute('data-wishlist-handle') || toggleBtn.getAttribute('data-wishlist-toggle');
        if (handle) window.Wishlist.toggle(handle);
      }
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        const handle = removeBtn.getAttribute('data-wishlist-remove');
        if (handle) {
          window.Wishlist.remove(handle);
          this.render();
        }
      }
      const addToCartBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if (addToCartBtn) {
        e.preventDefault();
        if (addToCartBtn.disabled || addToCartBtn.getAttribute('aria-disabled') === 'true') return;
        const variantId = addToCartBtn.getAttribute('data-variant-id');
        if (variantId) this.addToCart(variantId, addToCartBtn);
      }
    });

    document.addEventListener(WISHLIST_EVENT, () => {
      this.updateEmptyState();
      this.render();
      this.updateHeaderCount();
    });

    this.setHeaderAccessibility();
    this.updateEmptyState();
    this.updateHeaderCount();
  }

  setHeaderAccessibility() {
    const link = document.querySelector('#wishlist-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('keydown', (e) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this.open();
      }
    });
  }

  open() {
    this.classList.add('active');
    this.render();
    document.body.classList.add('overflow-hidden');
    const focusEl = this.querySelector('.wishlist-drawer__close') || this.inner;
    if (focusEl) focusEl.focus();
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    const prev = document.querySelector('[data-wishlist-open]');
    if (prev) prev.focus();
  }

  updateEmptyState() {
    const list = getWishlist();
    const isEmpty = list.length === 0;
    this.classList.toggle('is-empty', isEmpty);
    if (this.emptyState) this.emptyState.hidden = !isEmpty;
    if (this.itemsContainer) this.itemsContainer.hidden = isEmpty;
  }

  addToCart(variantId, buttonEl) {
    const variantIdStr = String(variantId || '').trim();
    if (!variantIdStr) return;
    if (this._addedVariantIds.has(variantIdStr)) return;

    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantIdStr);
    formData.append('quantity', 1);
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(buttonEl);
    }
    const cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
    const cartAddJsUrl = '/cart/add.js';
    const urlToUse = cartDrawer ? cartAddUrl : cartAddJsUrl;

    const config = (typeof fetchConfig === 'function') ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    if (config.headers['Content-Type']) delete config.headers['Content-Type'];
    config.body = formData;

    if (buttonEl) {
      buttonEl.disabled = true;
      buttonEl.classList.add('loading');
    }

    fetch(urlToUse, config)
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          if (buttonEl) {
            buttonEl.disabled = false;
            buttonEl.classList.remove('loading');
            buttonEl.textContent = response.description || 'Error';
          }
          return;
        }
        if (cartDrawer && response.sections) {
          cartDrawer.renderContents(response);
        } else if (cartDrawer && !response.sections) {
        } else if (!cartDrawer) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist', productVariantId: variantIdStr, cartData: response });
          }
        }

        if (buttonEl) {
          buttonEl.classList.remove('loading');
          this._addedVariantIds.add(variantIdStr);
          buttonEl.textContent = this.strings.addedCta || 'Added!';
          buttonEl.disabled = true;
          buttonEl.setAttribute('aria-disabled', 'true');
        }
      })
      .catch(() => {
        if (buttonEl) {
          buttonEl.disabled = false;
          buttonEl.classList.remove('loading');
        }
      });
  }

  updateHeaderCount() {
    const count = getWishlist().length;
    const str = count > 99 ? '99+' : String(count);
    const bubble = document.querySelector('.wishlist-count-bubble');
    const bubbleSpan = document.querySelector('.wishlist-count-bubble span');
    const textCount = document.querySelector('.wishlist-count-number');
    if (bubble) bubble.classList.toggle('hidden', count === 0);
    if (bubbleSpan) bubbleSpan.textContent = str;
    if (textCount) textCount.textContent = str;
  }

  async render() {
    const list = getWishlist();
    this.updateEmptyState();
    if (!this.itemsContainer || list.length === 0) {
      return;
    }

    this.itemsContainer.hidden = false;
    this.itemsContainer.innerHTML = '<div class="wishlist-drawer__loading"><span class="spinner"></span></div>';

    const base = (window.shopUrl || window.Shopify?.routes?.root || '').replace(/\/$/, '') || '';
    const baseUrl = base ? base + '/' : '/';
    const htmlParts = [];

    for (const handle of list) {
      try {
        const url = `${baseUrl}products/${handle}.json`;
        const res = await fetch(url);
        if (!res.ok) continue;
        const data = await res.json();
        const product = data.product;
        if (!product) continue;

        const img = product.featured_image || (product.images && product.images[0]);
        const imgSrc = typeof img === 'string' ? img : (img ? (img.src || img.url) : '');
        const imgAlt = (img && typeof img === 'object' && img.alt) ? img.alt : product.title;
        const variants = product.variants || [];
        const variant = variants.find((v) => v.available) || variants[0] || null;
        const price = variant ? variant.price : (product.price || 0);
        const compareAt = variant ? variant.compare_at_price : (product.compare_at_price || null);
        const priceFormatted = this.formatMoney(price);
        const compareFormatted = compareAt && compareAt > price ? this.formatMoney(compareAt) : '';
        const variantId = variant ? variant.id : '';
        const isAvailable = variant ? (variant.available !== false) : true;
        const addToCartLabel = this.strings.addToCart || 'Add to cart';
        const soldOutLabel = (typeof window.variantStrings !== 'undefined' && window.variantStrings.soldOut) ? window.variantStrings.soldOut : 'Sold out';
        const addedCta = this.strings.addedCta || 'Added!';
        const isAlreadyAdded = variantId ? this._addedVariantIds.has(String(variantId)) : false;

        htmlParts.push(`
          <div class="wishlist-drawer__item" data-product-handle="${this.escapeAttr(product.handle)}">
            <a href="${this.escapeAttr(baseUrl + 'products/' + product.handle)}" class="wishlist-drawer__item-link">
              <div class="wishlist-drawer__item-image">
                ${imgSrc ? `<img src="${this.escapeAttr(imgSrc)}" alt="${this.escapeAttr(imgAlt)}" loading="lazy" width="120" height="120">` : ''}
              </div>
              <div class="wishlist-drawer__item-details">
                <span class="wishlist-drawer__item-title">${this.escapeHtml(product.title)}</span>
                <div class="wishlist-drawer__item-price">
                  ${compareFormatted ? `<s class="wishlist-drawer__item-compare">${compareFormatted}</s> ` : ''}
                  <span>${priceFormatted}</span>
                </div>
              </div>
            </a>
            <div class="wishlist-drawer__item-actions">
              ${variantId ? `
              <button type="button" class="wishlist-drawer__item-atc button button--primary" data-wishlist-add-to-cart data-variant-id="${this.escapeAttr(String(variantId))}" ${(isAvailable && !isAlreadyAdded) ? '' : ' disabled'} aria-label="${this.escapeAttr(addToCartLabel)}">
                ${isAlreadyAdded ? this.escapeHtml(addedCta) : (isAvailable ? this.escapeHtml(addToCartLabel) : this.escapeHtml(soldOutLabel))}
              </button>
              ` : ''}
              <button type="button" class="wishlist-drawer__item-remove" data-wishlist-remove="${this.escapeAttr(product.handle)}" aria-label="${this.escapeAttr((this.strings.remove || 'Remove from wishlist'))}">
                ${this.escapeHtml(this.strings.removeCta || 'Remove')}
              </button>
            </div>
          </div>
        `);
      } catch (err) {
        console.warn('Wishlist: could not load product', handle, err);
      }
    }

    this.itemsContainer.innerHTML = htmlParts.length
      ? htmlParts.join('')
      : '<p class="wishlist-drawer__empty-inner">' + (this.strings.empty || 'Your wishlist is empty') + '</p>';
    this.updateEmptyState();
  }

  escapeAttr(str) {
    if (str == null) return '';
    const s = String(str);
    return s
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  escapeHtml(str) {
    if (str == null) return '';
    const s = String(str);
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  formatMoney(price) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    const num = Number(price);
    if (Number.isNaN(num)) return '';
    const isDollars = typeof price === 'string' && String(price).includes('.') ||
      (num !== Math.floor(num)) ||
      (num > 0 && num < 100);
    const amount = isDollars ? num.toFixed(2) : (num / 100).toFixed(2);
    return typeof window.themeCurrency === 'string' ? window.themeCurrency + amount : '$' + amount;
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);

function updateWishlistButtons() {
  const list = getWishlist();
  document.querySelectorAll('[data-wishlist-toggle], [data-wishlist-handle]').forEach((el) => {
    const handle = el.getAttribute('data-wishlist-handle') || el.getAttribute('data-wishlist-toggle');
    if (!handle) return;
    const inList = list.includes(handle);
    el.setAttribute('aria-pressed', inList ? 'true' : 'false');
    el.classList.toggle('is-in-wishlist', inList);
  });
}

document.addEventListener(WISHLIST_EVENT, updateWishlistButtons);
document.addEventListener('DOMContentLoaded', updateWishlistButtons);
